/**
 * The wizard's own step vocabulary (REV-21354).
 *
 * ⚠️ NOT the launch pipeline's `ClosingSetupStepKey`. These are the screens the trustee walks; those
 * are the work the server does afterwards. They were one list in an early draft and the two kept
 * being confused.
 */
export type ClosingWizardStepKey = 'ACCOUNTING' | 'DOCUMENTS';

/** One entry in the stepper across the top. */
export interface ClosingWizardStep {
    readonly key: ClosingWizardStepKey;
    /** Already translated by the container — this view holds no vocabulary of its own. */
    readonly label: string;
}

/** How the accounting data is to reach the closing. */
export type ClosingWizardAccountingChoice = 'API' | 'FILE';

/**
 * The company's connected ERP, as the accounting step shows it — taken from the integrations'
 * vendor catalogue, so the logo and colour are the ones the rest of the app shows.
 */
export interface ClosingWizardErp {
    readonly name: string;
    readonly logoSrc: string;
    readonly bannerColor: string;
    /** The company the closing belongs to. */
    readonly company: string;
    /** Where the connection is managed: the organisation's settings, or the company's own page. */
    readonly managedIn: 'organisation' | 'company';
}

/** One period the start will load, already formatted by the container. */
export interface ClosingWizardYear {
    /** «01.01.2025 - 31.12.2025» */
    readonly label: string;
    /** Whether aumico already holds this year's journal. */
    readonly present: boolean;
}

/**
 * REV-22007 — where the accounting file (a trial balance) stands on the accounting step.
 *
 * `refused` means the server read it and said no — nothing was stored, and the refusal list says
 * why. `ready` means it is stored and previewed; the import itself runs after the start.
 */
export type ClosingWizardFileState = 'idle' | 'uploading' | 'ready' | 'refused';

/** The preview card. Every string is already formatted by the container. */
export interface ClosingWizardFilePreview {
    readonly fileName: string;
    readonly accounts: number;
    /** "0.00" — formatted; the view never computes a sum. */
    readonly difference: string;
    /** Translated warning lines ("Zeile 58: ohne Kontonummer übersprungen"). */
    readonly warnings: readonly string[];
}

/** One currency the accounting step offers: ISO code and its name in the UI language. */
export interface ClosingWizardCurrencyOption {
    readonly id: string;
    readonly label: string;
}
